import { userToken } from '@/features/auth/helpers';
import { isSessionExpired, notifySessionExpired } from '@/features/auth/session-recovery';
import { getSession } from 'next-auth/react';
import { unstable_rethrow } from 'next/navigation';

import { ApiError, HttpStatusCode } from './ApiError';

export const SERVER_ORIGIN = process.env.ORIGIN ?? '';

/** Absolutises a site-relative path for the places that must emit a full URL: sitemap, robots, canonical tags. */
export const absoluteUrl = (path: string) => `${SERVER_ORIGIN}${path}`;

export const BASE_URL =
  globalThis.window === undefined ? process.env.NEXT_PUBLIC_BASE_URL_SERVER : process.env.NEXT_PUBLIC_BASE_URL;

export const BASE_API_URL = `${BASE_URL}/api`;
export const ORIGIN =
  globalThis.window !== undefined && globalThis.location.origin != null ? globalThis.location.origin : '';

export const REPO_URL = process.env.NEXT_PUBLIC_REPO_URL;

export const baseHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'Access-Control-Allow-Origin': ORIGIN,
};

type ParamValue = string | number | boolean | null | undefined;
type ParamRecord = Record<string, ParamValue | ParamValue[]>;

const stringfyBody = (body: unknown) => (body ? JSON.stringify(body) : undefined);

export const buildURLWithParams = (url: string, params: ParamRecord) => {
  const filteredParams = Object.fromEntries(
    Object.entries(params)
      .filter(
        (entry): entry is [string, string | number | boolean | (string | number | boolean)[]] =>
          entry[1] !== null && entry[1] !== undefined,
      )
      .map(([key, value]) => [key, String(value)]),
  );
  return `${url}?${new URLSearchParams(filteredParams).toString()}`;
};

export async function parseResponse(response: Response) {
  if (!response.ok) {
    let message = response.statusText;
    try {
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        const error = await response.json();

        message = error.detail ?? error.title ?? error.message ?? message;
      } else {
        const text = await response.text();
        if (text.includes('<!DOCTYPE') || text.includes('<html')) {
          message = `${response.statusText} (Server returned HTML instead of JSON)`;
        } else {
          message = text || response.statusText;
        }
      }
    } catch {
      // ignore parse error
    }
    throw new ApiError(response.status, message);
  }
  const string = await response.text();
  return string === '' ? {} : JSON.parse(string);
}

// Cached to the token's own expiry rather than a fixed TTL: a flat window outlives a token cached
// near the end of its life, and every request made in the overhang comes back 401.
let cachedClientToken: { token: string; validUntilMs: number } | null = null;
let inFlightTokenRead: Promise<string | null> | null = null;
// Short-circuits token reads and 401 replays once the session is beyond repair. Without it the tab
// keeps hammering /api/auth/session and the backend for as long as the re-authentication dialog is
// up, because every React Query refetch walks the same recovery path again.
let isSessionDead = false;

// Covers the round trip between reading the cache and the backend validating the token.
const TOKEN_CACHE_SAFETY_MS = 10_000;

function markSessionDead() {
  cachedClientToken = null;
  inFlightTokenRead = null;
  isSessionDead = true;
  notifySessionExpired();
}

// Called once re-authentication has succeeded: drops the dead bearer and lifts the short-circuit.
export function clearCachedClientToken() {
  cachedClientToken = null;
  inFlightTokenRead = null;
  isSessionDead = false;
}

async function fetchClientToken(): Promise<string | null> {
  // Reading the session runs the jwt callback server-side, which refreshes the token when it is close
  // to expiry. broadcast: false because the message is delivered back to this document's own
  // SessionProvider, which answers it with a second /api/auth/session read and a full tree re-render.
  const session = await getSession({ broadcast: false });

  if (isSessionExpired(session)) {
    markSessionDead();
    return null;
  }

  if (session?.accessToken == null) return null;

  if (session.expiresAt != null) {
    cachedClientToken = { token: session.accessToken, validUntilMs: session.expiresAt * 1000 - TOKEN_CACHE_SAFETY_MS };
  }

  return session.accessToken;
}

async function readClientToken(): Promise<string | null> {
  if (isSessionDead) return null;

  if (cachedClientToken != null && Date.now() < cachedClientToken.validUntilMs) {
    return cachedClientToken.token;
  }

  // Requests firing together on a cold cache would each read the session, and every read that lands
  // in the refresh window sends its own refresh grant — rotating the token out from under the others.
  inFlightTokenRead ??= fetchClientToken().finally(() => {
    inFlightTokenRead = null;
  });

  return await inFlightTokenRead;
}

async function buildHeaders(config?: RequestInit): Promise<Record<string, string>> {
  const headers: Record<string, string> = { ...baseHeaders, ...Object.fromEntries(new Headers(config?.headers)) };
  // `new Headers` lower-cases every name, so a caller-supplied bearer arrives as `authorization` and
  // has to be matched case-insensitively — writing `Authorization` alongside it would send both.
  const carriesBearer = Object.keys(headers).some((key) => key.toLowerCase() === 'authorization');

  // A caller that already holds the session hands its bearer in rather than making this read the
  // session cookie again: server-side that read is another JWE decrypt per request.
  if (!carriesBearer) {
    const token = globalThis.window === undefined ? await userToken() : await readClientToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  // The boundary marker is generated by fetch itself, so a Content-Type set here would be missing it.
  if (config?.body instanceof FormData) {
    for (const key of Object.keys(headers)) {
      if (key.toLowerCase() === 'content-type') delete headers[key];
    }
  }

  return headers;
}

export function normalizeUrl(url: string): string {
  return url.startsWith('/') ? url : `/${url}`;
}

/**
 * The single authorized entry point to the backend — JSON, blobs and PDFs all go through it, so the
 * 401 recovery below is shared rather than reimplemented per response type.
 */
export async function authorizedFetch(url: string, config?: RequestInit): Promise<Response> {
  const fullUrl = `${BASE_URL}/api${normalizeUrl(url)}`;
  const send = async () => {
    const headers = await buildHeaders(config);
    const sentToken = Object.keys(headers).some((key) => key.toLowerCase() === 'authorization');
    return { response: await fetch(fullUrl, { ...config, headers }), sentToken };
  };

  const { response, sentToken } = await send();
  // A 401 on a request that carried no bearer is an anonymous visitor hitting a protected endpoint,
  // not a session that died. Server-side calls are left alone too: their token comes from the request
  // being served, so one user's failure must not mark the shared module state dead.
  const canRecover = globalThis.window !== undefined && sentToken && !isSessionDead;
  if (response.status !== HttpStatusCode.Unauthorized || !canRecover) return response;

  // Replaying is safe even for POST/PATCH: a 401 is rejected at authentication, before the backend
  // acts on the body. Dropping the cached bearer forces a session read, which refreshes the token, so
  // the common case — a token that expired between two requests — recovers without the user noticing.
  clearCachedClientToken();
  const { response: retried } = await send();
  // A second 401 with a freshly read token means the session itself is gone, not merely stale.
  if (retried.status === HttpStatusCode.Unauthorized) markSessionDead();

  return retried;
}

export async function request<TResponse>(url: string, config?: RequestInit): Promise<TResponse> {
  try {
    return await parseResponse(await authorizedFetch(url, config));
  } catch (error: unknown) {
    // Server-side calls read the request's cookies to get their bearer, and during prerendering that
    // read throws a framework error which Next.js expects back to mark the route dynamic. Wrapping it
    // in an ApiError hides it, and the build fails with "couldn't be rendered statically".
    unstable_rethrow(error);
    if (error instanceof ApiError) {
      throw error;
    }
    if (error instanceof Error) {
      throw new ApiError(error);
    }
    throw new ApiError(HttpStatusCode.InternalServerError, String(error));
  }
}

const api = {
  get: async <TResponse>(url: string, config?: RequestInit) => await request<TResponse>(url, config),

  post: async <TResponse>(url: string, body?: unknown, config?: RequestInit) =>
    await request<TResponse>(url, {
      ...config,
      method: 'POST',
      body: body instanceof FormData ? body : stringfyBody(body),
    }),

  patch: async <TResponse>(url: string, body?: unknown, config?: RequestInit) =>
    await request<TResponse>(url, {
      ...config,
      method: 'PATCH',
      body: body instanceof FormData ? body : stringfyBody(body),
    }),

  put: async <TResponse>(url: string, body?: unknown, config?: RequestInit) =>
    await request<TResponse>(url, {
      ...config,
      method: 'PUT',
      body: body instanceof FormData ? body : stringfyBody(body),
    }),

  delete: async <TResponse>(url: string, body?: unknown, config?: RequestInit) =>
    await request<TResponse>(url, {
      ...config,
      method: 'DELETE',
      body: body instanceof FormData ? body : stringfyBody(body),
    }),

  downloadFile: async (url: string, body?: unknown, config?: RequestInit) => {
    const response = await authorizedFetch(url, {
      ...config,
      method: 'POST',
      body: body instanceof FormData ? body : stringfyBody(body),
    });

    if (!response.ok) {
      let message = response.statusText;
      try {
        const text = await response.text();
        message = text || response.statusText;
      } catch {
        /* ignore */
      }
      throw new ApiError(response.status, message);
    }

    const contentDisposition = response.headers.get('content-disposition');
    const filenameMatch = contentDisposition?.match(/filename="?([^";]+)"?/);
    const filename = filenameMatch ? filenameMatch[1] : 'download.xlsx';

    const blob = await response.blob();
    return { blob, filename };
  },

  isCancel: (error: unknown) =>
    error instanceof ApiError && error.cause instanceof Error && error.cause.name === 'AbortError',
};

export default api;
